import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { CommonService } from '../services/common.service';

export type ApiConnectivityModeSlug = 'poll' | 'webhook' | 'push';

@Component({
  selector: 'app-api-contract-builder',
  templateUrl: './api-contract-builder.component.html',
  styleUrls: ['./api-contract-builder.component.scss']
})
export class ApiContractBuilderComponent implements OnInit, OnChanges {
  @Input() authenticationType: 'token' | 'basic' = 'basic';
  @Input() connectivityModeSlug: ApiConnectivityModeSlug = 'poll';
  @Input() interfaceDirection: 'inbound' | 'outbound' = 'inbound';
  @Input() messageType: string = '';
  @Input() contractData: any = null;
  @Input() resetVersion: number = 0;
  @Input() readOnly: boolean = false;
  @Output() saveContract = new EventEmitter<any>();
  @Output() headersBulkEditActiveChange = new EventEmitter<boolean>();

  contractForm: FormGroup = this.fb.group({
    apiCards: this.fb.array([])
  });

  methodOptions: string[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
  bodyTypeOptions: string[] = ['none', 'text', 'json', 'xml'];
  testStates: Array<{ loading: boolean; status: string; response: string; isError: boolean }> = [];
  headersBulkEditMode: boolean[] = [];
  headersBulkText: string[] = [];
  showAuthPassword = false;
  private suppressEmit = false;

  constructor(private fb: FormBuilder, private service: CommonService) {}

  ngOnInit(): void {
    this.rebuildFormFromInputs();
    this.contractForm.valueChanges.subscribe(value => {
      if (!this.suppressEmit) {
        this.saveContract.emit(value);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['authenticationType'] ||
      changes['connectivityModeSlug'] ||
      changes['interfaceDirection'] ||
      changes['messageType'] ||
      changes['contractData'] ||
      changes['resetVersion'] ||
      changes['readOnly']
    ) {
      this.rebuildFormFromInputs();
    }
  }

  get apiCards(): FormArray {
    return this.contractForm.get('apiCards') as FormArray;
  }

  isModeCardUrlRequired(cardIndex: number): boolean {
    const card = this.apiCards.at(cardIndex) as FormGroup;
    const key = (card.get('cardKey')?.value || '').toLowerCase();
    return key === 'poll' || key === 'push';
  }

  getCardTitle(card: FormGroup): string {
    const key = (card.get('cardKey')?.value || '').toLowerCase();
    if (key === 'token') {
      return 'Token';
    }
    if (key === 'refreshtoken') {
      return 'Refresh Token';
    }
    if (key === 'poll') {
      return 'Polling';
    }
    if (key === 'webhook') {
      return 'Webhook';
    }
    if (key === 'push') {
      return 'Push';
    }
    return key || 'API';
  }

  queryParamsAt(cardIndex: number): FormArray {
    return this.apiCards.at(cardIndex).get('queryParams') as FormArray;
  }

  headersAt(cardIndex: number): FormArray {
    return this.apiCards.at(cardIndex).get('headers') as FormArray;
  }

  toggleAuthPasswordVisibility(): void {
    this.showAuthPassword = !this.showAuthPassword;
  }

  addQueryParam(cardIndex: number): void {
    if (this.readOnly) {
      return;
    }
    this.queryParamsAt(cardIndex).push(this.createKeyValueGroup());
  }

  removeQueryParam(cardIndex: number, index: number): void {
    if (this.readOnly) {
      return;
    }
    this.queryParamsAt(cardIndex).removeAt(index);
  }

  addHeader(cardIndex: number): void {
    if (this.readOnly || this.headersBulkEditMode[cardIndex]) {
      return;
    }
    this.headersAt(cardIndex).push(this.createKeyValueGroup());
  }

  removeHeader(cardIndex: number, index: number): void {
    if (this.readOnly || this.headersBulkEditMode[cardIndex]) {
      return;
    }
    this.headersAt(cardIndex).removeAt(index);
  }

  toggleHeadersBulkEdit(cardIndex: number): void {
    if (this.readOnly) {
      return;
    }

    if (this.headersBulkEditMode[cardIndex]) {
      this.applyHeadersBulkText(cardIndex);
      this.headersBulkEditMode[cardIndex] = false;
      this.headersBulkText[cardIndex] = '';
    } else {
      this.headersBulkText[cardIndex] = this.serializeHeaders(cardIndex);
      this.headersBulkEditMode[cardIndex] = true;
    }
    this.emitHeadersBulkEditActive();
  }

  isAnyHeadersBulkEditActive(): boolean {
    return this.headersBulkEditMode.some(Boolean);
  }

  onMethodChange(cardIndex: number): void {
    const card = this.apiCards.at(cardIndex) as FormGroup;
    this.applyDefaultsFromBodyType(card);
  }

  onBodyTypeChange(cardIndex: number): void {
    const card = this.apiCards.at(cardIndex) as FormGroup;
    this.applyDefaultsFromBodyType(card);
  }

  onUrlInput(cardIndex: number): void {
    const card = this.apiCards.at(cardIndex) as FormGroup;
    const rawUrl = (card.get('url')?.value || '').trim();
    const paramsArray = this.queryParamsAt(cardIndex);

    if (!rawUrl || rawUrl.indexOf('?') === -1) {
      paramsArray.clear();
      return;
    }

    const queryString = rawUrl.substring(rawUrl.indexOf('?') + 1);
    const searchParams = new URLSearchParams(queryString);

    paramsArray.clear();
    searchParams.forEach((value, key) => {
      paramsArray.push(this.createKeyValueGroupWithValues(key, value));
    });
  }

  async onTestRequest(cardIndex: number): Promise<void> {
    if (this.readOnly || this.headersBulkEditMode[cardIndex]) {
      return;
    }
    const card = this.apiCards.at(cardIndex) as FormGroup;
    const method = (card.get('method')?.value || 'GET').toUpperCase();
    const url = (card.get('url')?.value || '').trim();
    const state = this.ensureTestState(cardIndex);

    if (!url) {
      state.isError = true;
      state.status = 'Validation';
      state.response = 'URL is required to test request.';
      return;
    }

    state.loading = true;
    state.status = '';
    state.response = '';
    state.isError = false;

    try {
      const headers = this.buildDictionaryFromArray(this.headersAt(cardIndex).value);
      this.appendBasicAuthorizationHeaderIfRequired(card, headers);
      const params = this.buildDictionaryFromArray(this.queryParamsAt(cardIndex).value);
      const body = this.shouldAllowBody(method) ? (card.get('body')?.value || '') : '';
      const contentType = this.shouldAllowBody(method) ? (card.get('contentType')?.value || '') : '';

      const response = await this.service.executeDynamicHttpRequest({
        method,
        url,
        headers,
        params,
        body,
        contentType
      });

      const httpStatus = response?.status || 0;
      state.isError = httpStatus >= 400;
      state.status = `${httpStatus || ''} ${response?.statusText || ''}`.trim();
      state.response = this.stringifyResponseBody(response?.body);
    } catch (error: any) {
      state.isError = true;
      state.status = `${error?.status || 'Error'} ${error?.statusText || ''}`.trim();
      state.response = this.stringifyResponseBody(error?.error || error?.message || 'Request failed');
    } finally {
      state.loading = false;
    }
  }

  copyResponse(cardIndex: number): void {
    const responseText = this.testStates[cardIndex]?.response || '';
    if (!responseText) {
      return;
    }

    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(responseText);
    }
  }

  shouldShowBodyEditor(cardIndex: number): boolean {
    const card = this.apiCards.at(cardIndex) as FormGroup;
    const method = (card.get('method')?.value || 'GET').toUpperCase();
    const bodyType = (card.get('bodyType')?.value || 'none').toLowerCase();
    return this.shouldAllowBody(method) && bodyType !== 'none';
  }

  isPushCard(cardIndex: number): boolean {
    const card = this.apiCards.at(cardIndex) as FormGroup;
    return (card.get('cardKey')?.value || '').toLowerCase() === 'push';
  }

  canUseBodyMethod(cardIndex: number): boolean {
    const card = this.apiCards.at(cardIndex) as FormGroup;
    const method = (card.get('method')?.value || 'GET').toUpperCase();
    return this.shouldAllowBody(method);
  }

  private initializeCards(): void {
    this.apiCards.clear();
    this.testStates = [];
    this.headersBulkEditMode = [];
    this.headersBulkText = [];

    const templates = this.getTemplates();
    templates.forEach(template => {
      const card = this.createCard(template.cardKey, template.method);
      this.apiCards.push(card);
      this.testStates.push({ loading: false, status: '', response: '', isError: false });
      this.headersBulkEditMode.push(false);
      this.headersBulkText.push('');
      this.applyDefaultsFromBodyType(card);
    });
  }

  private rebuildFormFromInputs(): void {
    this.suppressEmit = true;
    this.initializeCards();
    this.applyContractData();
    this.applyReadOnlyState();
    this.suppressEmit = false;
    this.saveContract.emit(this.contractForm.value);
    this.emitHeadersBulkEditActive();
  }

  private serializeHeaders(cardIndex: number): string {
    return this.headersAt(cardIndex).controls
      .map(control => {
        const key = (control.get('key')?.value || '').trim();
        const value = control.get('value')?.value ?? '';
        return key || value ? `${key}: ${value}` : '';
      })
      .filter(Boolean)
      .join('\n');
  }

  private applyHeadersBulkText(cardIndex: number): void {
    const lines = (this.headersBulkText[cardIndex] || '').split(/\r?\n/);
    const headers = this.headersAt(cardIndex);
    headers.clear();

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        continue;
      }

      const colonIndex = trimmed.indexOf(':');
      const key = colonIndex === -1 ? trimmed : trimmed.slice(0, colonIndex).trim();
      const value = colonIndex === -1 ? '' : trimmed.slice(colonIndex + 1).trim();
      headers.push(this.createKeyValueGroupWithValues(key, value));
    }
  }

  private emitHeadersBulkEditActive(): void {
    this.headersBulkEditActiveChange.emit(this.isAnyHeadersBulkEditActive());
  }

  private applyReadOnlyState(): void {
    if (this.readOnly) {
      this.showAuthPassword = false;
      this.contractForm.disable({ emitEvent: false });
      return;
    }
    if (this.contractForm.disabled) {
      this.contractForm.enable({ emitEvent: false });
    }
  }

  private applyContractData(): void {
    const incomingCards = Array.isArray(this.contractData?.apiCards) ? this.contractData.apiCards : [];
    if (incomingCards.length === 0) {
      return;
    }

    this.apiCards.controls.forEach((control, index) => {
      const card = control as FormGroup;
      const cardKey = (card.get('cardKey')?.value || '').toLowerCase();
      const incoming =
        incomingCards.find((x: any) => (x?.cardKey || '').toLowerCase() === cardKey) || incomingCards[index];
      if (!incoming) {
        return;
      }

      card.patchValue(
        {
          method: incoming.method || card.get('method')?.value,
          url: incoming.url || '',
          contentType: incoming.contentType || '',
          bodyType: incoming.bodyType || card.get('bodyType')?.value,
          body: incoming.body || '',
          authUsername: incoming.authUsername || '',
          authPassword: incoming.authPassword || '',
          isSendBase64: this.parseIsSendBase64(incoming.isSendBase64)
        },
        { emitEvent: false }
      );

      const paramsArray = this.queryParamsAt(index);
      paramsArray.clear();
      (incoming.queryParams || []).forEach((item: any) =>
        paramsArray.push(this.createKeyValueGroupWithValues(item?.key || '', item?.value || ''))
      );

      const headersArray = this.headersAt(index);
      headersArray.clear();
      (incoming.headers || []).forEach((item: any) =>
        headersArray.push(this.createKeyValueGroupWithValues(item?.key || '', item?.value || ''))
      );

      this.applyDefaultsFromBodyType(card);
    });
  }

  private getTemplates(): Array<{ cardKey: string; method: string }> {
    const mode = this.connectivityModeSlug || 'poll';
    const thirdCardMethod = this.interfaceDirection === 'outbound' ? 'POST' : 'GET';

    if (this.authenticationType === 'token') {
      return [
        { cardKey: 'token', method: 'GET' },
        { cardKey: 'refreshtoken', method: 'GET' },
        { cardKey: mode, method: thirdCardMethod }
      ];
    }

    return [{ cardKey: mode, method: thirdCardMethod }];
  }

  private createCard(cardKey: string, method: string): FormGroup {
    return this.fb.group({
      cardKey: [cardKey],
      method: [method],
      url: [''],
      contentType: ['application/json'],
      bodyType: [method === 'GET' ? 'none' : 'json'],
      body: [''],
      authUsername: [''],
      authPassword: [''],
      isSendBase64: [false],
      queryParams: this.fb.array([]),
      headers: this.fb.array([])
    });
  }

  private parseIsSendBase64(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value || '').trim().toLowerCase() === 'true';
  }

  private createKeyValueGroup(): FormGroup {
    return this.fb.group({
      key: [''],
      value: ['']
    });
  }

  private createKeyValueGroupWithValues(key: string, value: string): FormGroup {
    return this.fb.group({
      key: [key],
      value: [value]
    });
  }

  private shouldAllowBody(method: string): boolean {
    const upper = (method || '').toUpperCase();
    return upper === 'POST' || upper === 'PUT' || upper === 'PATCH';
  }

  private applyDefaultsFromBodyType(card: FormGroup): void {
    const method = (card.get('method')?.value || 'GET').toUpperCase();
    const bodyTypeControl = card.get('bodyType');
    const contentTypeControl = card.get('contentType');

    if (!this.shouldAllowBody(method)) {
      bodyTypeControl?.setValue('none', { emitEvent: false });
      contentTypeControl?.setValue('', { emitEvent: false });
      card.get('body')?.setValue('', { emitEvent: false });
      return;
    }

    const bodyType = (bodyTypeControl?.value || 'none').toLowerCase();
    const contentTypeMap: Record<string, string> = {
      json: 'application/json',
      xml: 'application/xml',
      text: 'text/plain',
      none: ''
    };
    contentTypeControl?.setValue(contentTypeMap[bodyType] || '', { emitEvent: false });
  }

  private buildDictionaryFromArray(items: Array<{ key: string; value: string }>): Record<string, string> {
    return (items || []).reduce((acc: Record<string, string>, item) => {
      const key = (item?.key || '').trim();
      if (key) {
        acc[key] = item?.value || '';
      }
      return acc;
    }, {});
  }

  private appendBasicAuthorizationHeaderIfRequired(card: FormGroup, headers: Record<string, string>): void {
    if (this.authenticationType !== 'basic') {
      return;
    }

    const username = (card.get('authUsername')?.value || '').trim();
    const password = (card.get('authPassword')?.value || '').trim();
    if (!username || !password) {
      return;
    }

    const hasAuthorization = Object.keys(headers).some(key => key.toLowerCase() === 'authorization');
    if (hasAuthorization) {
      return;
    }

    headers['Authorization'] = `Basic ${btoa(`${username}:${password}`)}`;
  }

  private stringifyResponseBody(body: any): string {
    if (body === undefined || body === null) {
      return '';
    }
    if (typeof body === 'string') {
      return body;
    }

    try {
      return JSON.stringify(body, null, 2);
    } catch {
      return String(body);
    }
  }

  private ensureTestState(cardIndex: number): { loading: boolean; status: string; response: string; isError: boolean } {
    if (!this.testStates[cardIndex]) {
      this.testStates[cardIndex] = { loading: false, status: '', response: '', isError: false };
    }
    return this.testStates[cardIndex];
  }
}
